import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, BarChart, Bar, ReferenceLine, Cell } from "recharts";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

const BRAND = "#6E3FFC";
const CPI = "#F59E0B";
const n = (v, d = 1) => (typeof v === "number" ? v.toFixed(d) : "—");
const pct = (v, d = 1) => (typeof v === "number" ? `${v > 0 ? "+" : ""}${v.toFixed(d)}%` : "—");
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
const unit = (u) => (u === "g" ? "/100g" : u === "ml" ? "/100ml" : "/unit");

function Kpi({ label, value, sub, tone, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-head text-2xl font-extrabold tabular-nums ${tone || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const Section = ({ title, children, note, testId }) => (
  <section data-testid={testId} className="mt-10">
    <h2 className="font-head text-lg md:text-xl font-bold text-slate-900">{title}</h2>
    {note && <p className="text-xs text-slate-500 mt-1">{note}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

function Movers({ title, rows, up, hasPrev }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 ${up ? "text-red-600" : "text-emerald-700"}`}>
        {up ? <TrendUp size={16} weight="bold" /> : <TrendDown size={16} weight="bold" />} {title}
      </div>
      {rows.length ? rows.map((m, i) => (
        <div key={i} className="flex items-center gap-3 border-t border-slate-100 px-4 py-2 text-sm">
          <span className="w-5 text-xs text-slate-400 tabular-nums">{i + 1}</span>
          <div className="min-w-0 flex-1"><div className="truncate font-medium">{m.item}</div><div className="text-[11px] text-slate-500">{m.category}</div></div>
          <span className="text-xs text-slate-500 tabular-nums">R{n(m.unit_price, 2)}{unit(m.unit)}</span>
          <span className={`w-16 text-right font-semibold tabular-nums ${up ? "text-red-600" : "text-emerald-700"}`}>{pct(m.change_pct)}</span>
        </div>
      )) : <div className="border-t border-slate-100 px-4 py-6 text-center text-xs text-slate-500">{hasPrev ? `No items ${up ? "rose" : "fell"} in price this month.` : "Available from the second month after the base period."}</div>}
    </div>
  );
}

export const HpiReportView = ({ report }) => {
  const d = report.data;
  const h = d.headline;
  const summary = report.summary_text || d.commentary;
  const chart = d.series.map((s) => ({ month: s.month, HPI: s.hpi, CPI: s.cpi }));
  const Arrow = h.mom > 0 ? TrendUp : h.mom < 0 ? TrendDown : Minus;
  return (
    <div data-testid="hpi-report-view">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-riq-primary">Sliply Household Price Index</div>
      <h1 className="font-head text-4xl sm:text-5xl font-extrabold text-slate-900 mt-2">{monthLabel(d.month)}</h1>
      <p className="text-sm text-slate-500 mt-2">Base {d.base_month} = 100 · {d.coverage.items_priced} of {d.coverage.basket_items} items priced · {d.coverage.observations} price observations · {d.coverage.stores} stores</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="hpi-kpi-index" label="Headline HPI" value={n(h.index)} sub={<span className="inline-flex items-center gap-1"><Arrow size={12} /> {pct(h.mom)} m/m</span>} />
        <Kpi testId="hpi-kpi-mom" label="Month-on-month" value={pct(h.mom)} tone={h.mom > 0 ? "text-red-600" : h.mom < 0 ? "text-emerald-700" : ""} />
        <Kpi testId="hpi-kpi-yoy" label="Year-on-year" value={pct(h.yoy)} />
        <Kpi testId="hpi-kpi-cpi" label="CPI (rebased)" value={n(h.cpi_index)} sub={h.cpi_published ? `CPI m/m ${pct(h.cpi_mom)}` : `CPI pending · latest ${d.cpi_latest || "n/a"}`} />
        <Kpi testId="hpi-kpi-gap" label="HPI vs CPI" value={typeof h.gap_pts === "number" ? `${h.gap_pts > 0 ? "+" : ""}${h.gap_pts.toFixed(1)} pts` : "—"} />
      </div>

      <p data-testid="hpi-summary" className="mt-6 text-base leading-relaxed text-slate-700 max-w-3xl">{summary}</p>

      <Section testId="hpi-section-chart" title="HPI vs official CPI" note={`CPI: Stats SA P0141 headline, rebased to ${d.cpi_base_month} = 100. Correlation of monthly changes: ${n(d.correlation.monthly_change_r, 2)} (${d.correlation.months} months).`}>
        <div className="h-72 rounded-2xl border border-slate-200 bg-white p-3">
          <ResponsiveContainer>
            <LineChart data={chart} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#EEF0F5" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <ReferenceLine y={100} stroke="#CBD5E1" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="HPI" stroke={BRAND} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="CPI" stroke={CPI} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {d.cpi_recent?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {d.cpi_recent.slice(-6).map((c) => (
              <span key={c.month} className="rounded-full bg-amber-50 px-3 py-1 text-amber-800 tabular-nums">CPI {c.month}: {n(c.cpi)} · y/y {pct(c.cpi_yoy)}</span>
            ))}
          </div>
        )}
      </Section>

      <Section testId="hpi-section-categories" title="Category indices" note="Contribution = the category's share of the headline month-on-month change, in percentage points.">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>{["Category", "Weight", "Items", "Index", "m/m", "y/y", "Contrib. (pp)", "CPI index", "CPI m/m", "Gap"].map((x, i) => <th key={x} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{x}</th>)}</tr>
            </thead>
            <tbody>
              {d.categories.map((c) => (
                <tr key={c.key} data-testid={`hpi-cat-row-${c.key}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium" title={`CPI series: ${c.cpi_series.join(", ")}`}>{c.name}</td>
                  <td className="px-3 text-right tabular-nums">{c.weight}%</td>
                  <td className="px-3 text-right tabular-nums">{c.items}</td>
                  <td className="px-3 text-right tabular-nums font-semibold">{n(c.index)}</td>
                  <td className="px-3 text-right tabular-nums">{pct(c.mom)}</td>
                  <td className="px-3 text-right tabular-nums">{pct(c.yoy)}</td>
                  <td className="px-3 text-right tabular-nums">{n(c.contribution_pp, 2)}</td>
                  <td className="px-3 text-right tabular-nums">{n(c.cpi_index)}</td>
                  <td className="px-3 text-right tabular-nums">{pct(c.cpi_mom)}</td>
                  <td className="px-3 text-right tabular-nums">{n(c.gap_pts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section testId="hpi-section-retailers" title="Retailer basket comparison" note="Each retailer's prices for the same items relative to the market median (100). Lower is cheaper.">
        {d.retailers.length ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-3" style={{ height: Math.max(160, d.retailers.length * 44) }}>
            <ResponsiveContainer>
              <BarChart data={d.retailers} layout="vertical" margin={{ left: 20, right: 30 }}>
                <XAxis type="number" domain={[(m) => Math.min(80, Math.floor(m - 5)), (m) => Math.max(120, Math.ceil(m + 5))]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="store" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, _k, p) => [`${v} (${p.payload.items} items)`, "Price level"]} />
                <ReferenceLine x={100} stroke="#94A3B8" strokeDasharray="4 4" />
                <Bar dataKey="price_level" radius={[0, 6, 6, 0]}>
                  {d.retailers.map((r, i) => <Cell key={r.store} fill={i === 0 ? "#10B981" : BRAND} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-sm text-slate-500">Not enough overlapping items yet.</p>}
      </Section>

      <Section testId="hpi-section-movers" title="Biggest movers this month">
        <div className="grid md:grid-cols-2 gap-4">
          <Movers title="Top 10 risers" rows={d.risers} up hasPrev={d.has_previous ?? h.mom != null} />
          <Movers title="Top 10 fallers" rows={d.fallers} hasPrev={d.has_previous ?? h.mom != null} />
        </div>
      </Section>

      <Section testId="hpi-section-coverage" title="Coverage">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Items priced" value={`${d.coverage.items_priced}/${d.coverage.basket_items}`} />
          <Kpi label="Compared with base" value={d.coverage.items_compared} />
          <Kpi label="Online · receipts" value={`${d.coverage.online} · ${d.coverage.receipt}`} />
          <Kpi label="Provinces" value={d.coverage.provinces.length || "National"} />
        </div>
      </Section>

      <Section testId="hpi-section-method" title="Methodology">
        <div className="space-y-3 text-sm leading-relaxed text-slate-700 max-w-3xl">
          {(d.methodology || []).map((p, i) => {
            const m = p.match(/^<b>(.*?)<\/b>\s*(.*)$/);
            return <p key={i}>{m ? <><strong>{m[1]}</strong> {m[2]}</> : p}</p>;
          })}
        </div>
        <p className="mt-4 text-xs text-slate-500">May be quoted with attribution: "Source: Sliply Household Price Index". CPI data © Statistics South Africa.</p>
      </Section>
    </div>
  );
};
